import React from 'react';
import PropTypes from 'prop-types';

const temperatureRanges = [
  { id: 'refrigeration', label: 'Refrigeration (2°C to 8°C)', min: 2, max: 8 },
  { id: 'freezing', label: 'Freezing (-18°C or lower)', min: -30, max: -18 },
  { id: 'custom', label: 'Custom Range', min: -30, max: 20 },
];

const IntendedUseStep = ({ formData, handleInputChange, errors }) => {
  const handleTemperatureRangeChange = (e) => {
    const selectedRange = temperatureRanges.find(range => range.id === e.target.value);
    handleInputChange({
      target: {
        name: 'temperatureRange',
        value: e.target.value
      }
    });
    if (selectedRange && selectedRange.id !== 'custom') {
      handleInputChange({
        target: {
          name: 'minTemperature',
          value: selectedRange.min
        }
      });
      handleInputChange({
        target: {
          name: 'maxTemperature',
          value: selectedRange.max
        }
      });
    }
  };

  return (
    <div>
      <div className="mb-4">
        <label htmlFor="temperatureRange" className="block text-gray-700 text-sm font-bold mb-2">
          Desired Temperature Range
        </label>
        <select
          id="temperatureRange"
          name="temperatureRange"
          value={formData.temperatureRange || ''}
          onChange={handleTemperatureRangeChange}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          required
        >
          <option value="">Select temperature range</option>
          {temperatureRanges.map((range) => (
            <option key={range.id} value={range.id}>
              {range.label}
            </option>
          ))}
        </select>
        {errors.temperatureRange && <p className="mt-1 text-sm text-red-500">{errors.temperatureRange}</p>}
      </div>

      {formData.temperatureRange === 'custom' && (
        <div className="mb-4 flex space-x-4">
          <div className="flex-1">
            <label htmlFor="minTemperature" className="block text-gray-700 text-sm font-bold mb-2">
              Minimum Temperature (°C)
            </label>
            <input
              type="number"
              id="minTemperature"
              name="minTemperature"
              value={formData.minTemperature || ''}
              onChange={handleInputChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              min="-30"
              max="20"
              required
            />
          </div>
          <div className="flex-1">
            <label htmlFor="maxTemperature" className="block text-gray-700 text-sm font-bold mb-2">
              Maximum Temperature (°C)
            </label>
            <input
              type="number"
              id="maxTemperature"
              name="maxTemperature"
              value={formData.maxTemperature || ''}
              onChange={handleInputChange}
              className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
              min="-30"
              max="20"
              required
            />
          </div>
        </div>
      )}

      <div className="mb-4">
        <label htmlFor="powerSource" className="block text-gray-700 text-sm font-bold mb-2">
          Power Source
        </label>
        <select
          id="powerSource"
          name="powerSource"
          value={formData.powerSource || ''}
          onChange={handleInputChange}
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          required
        >
          <option value="">Select power source</option>
          <option value="240V">240V Power Supply</option>
          <option value="generator">Generator Required</option>
          <option value="unsure">Unsure</option>
        </select>
        {errors.powerSource && <p className="mt-1 text-sm text-red-500">{errors.powerSource}</p>}
      </div>

      <div className="mb-4">
        <p className="block text-gray-700 text-sm font-bold mb-2">Additional Features</p>
        <div className="flex flex-wrap">
          <label className="inline-flex items-center mr-6 mb-2">
            <input
              type="checkbox"
              name="shelving"
              checked={formData.shelving || false}
              onChange={handleInputChange}
              className="form-checkbox h-5 w-5 text-blue-600"
            />
            <span className="ml-2 text-gray-700">Shelving</span>
          </label>
          <label className="inline-flex items-center mr-6 mb-2">
            <input
              type="checkbox"
              name="lighting"
              checked={formData.lighting || false}
              onChange={handleInputChange}
              className="form-checkbox h-5 w-5 text-blue-600"
            />
            <span className="ml-2 text-gray-700">Internal Lighting</span>
          </label>
          <label className="inline-flex items-center mr-6 mb-2">
            <input
              type="checkbox"
              name="security"
              checked={formData.security || false}
              onChange={handleInputChange}
              className="form-checkbox h-5 w-5 text-blue-600"
            />
            <span className="ml-2 text-gray-700">Security Features</span>
          </label>
        </div>
      </div>

      <div className="mb-4">
        <label htmlFor="guestCount" className="block text-gray-700 text-sm font-bold mb-2">
          Guest Count (if relevant)
        </label>
        <input
          type="number"
          id="guestCount"
          name="guestCount"
          value={formData.guestCount || ''}
          onChange={handleInputChange}
          placeholder="Enter expected guest count"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          min="0"
        />
      </div>

      <div className="mb-4">
        <label htmlFor="useComment" className="block text-gray-700 text-sm font-bold mb-2">
          Additional Comments on Intended Use
        </label>
        <textarea
          id="useComment"
          name="useComment"
          value={formData.useComment || ''}
          onChange={handleInputChange}
          placeholder="Enter any additional comments about how you intend to use the coolroom"
          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
          rows="3"
        />
      </div>
    </div>
  );
};

IntendedUseStep.propTypes = {
  formData: PropTypes.object.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
};

export default IntendedUseStep;